import { isValidEmail } from "@/components/news-alerts/newsAlertsHelpers";
import { Label } from "@/components/ui/field";
import { IconButton } from "@/components/ui/icon-button";
import { JollyTextField } from "@/components/ui/textfield";
import { cn } from "@/lib/utils";
import {
  addRecipientToNewsAlert,
  removeRecipientFromNewsAlert,
} from "@/store/news-alerts/news-alerts.actions";
import useNewsAlertsStore from "@/store/news-alerts/news-alerts.slice";
import { XIcon } from "@phosphor-icons/react";
import { useCallback, useMemo, useRef, useState } from "react";

const NewsAlertsRecipientsField = () => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const createEditNewsAlert = useNewsAlertsStore(
    (state) => state.createEditNewsAlert,
  );

  const emailList = useMemo(
    () => Array.from(createEditNewsAlert.recipients),
    [createEditNewsAlert.recipients],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["Enter", "Tab", ",", " "].includes(e.key)) {
        e.preventDefault();
        if (inputValue) {
          const newEmail = inputValue.trim();
          if (newEmail.length && !emailList.includes(newEmail)) {
            addRecipientToNewsAlert(newEmail);
            setInputValue("");
          }
        }
      }
    },
    [emailList, inputValue, addRecipientToNewsAlert],
  );

  const handleRemove = useCallback(
    (email: string) => {
      removeRecipientFromNewsAlert(email);
    },
    [removeRecipientFromNewsAlert],
  );

  return (
    <div className="flex flex-col gap-1">
      <Label
        aria-label="recipients-label"
        className="text-sm font-medium text-gray-700"
      >
        Recipients
      </Label>
      <div
        className="border border-slate-200 rounded-md px-3 py-1 flex flex-wrap items-center gap-2 focus-within:border-blue-500 cursor-text min-h-[40px]"
        onClick={() => inputRef.current?.focus()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        {/* chips inside the field */}
        {emailList.length > 0 &&
          emailList.map((email) => {
            const valid = isValidEmail(email);
            return (
              <div
                key={email}
                className={cn(
                  "flex flex-wrap items-center gap-1 px-2 py-0 rounded-md border text-xs",
                  {
                    "border-none bg-blue-50 text-blue-700": valid,
                    "border-none bg-red-50 text-red-700": !valid,
                  },
                )}
              >
                <span>{email}</span>
                <IconButton
                  variant="icon-only"
                  icon={<XIcon size={14} />}
                  onPressChange={() => handleRemove(email)}
                  aria-label="Remove email"
                  className={"p-0 text-gray-700"}
                />
              </div>
            );
          })}

        {/* actual input inside same box */}
        <div className="flex-1 min-w-[120px]">
          <JollyTextField
            size="sm"
            ref={inputRef}
            label={undefined}
            aria-label="recipients-input"
            description={undefined}
            placeholder={emailList.length ? "" : "Add recipients..."}
            value={inputValue}
            onChange={(value) => setInputValue(value)}
            onKeyDown={handleKeyDown}
            className="border-0 bg-transparent p-1 m-0 focus:ring-0 focus:outline-hidden h-auto"
            clearable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default NewsAlertsRecipientsField;
